import { useCallback, useEffect, useMemo, useState } from 'react';

import { emitter, EventsEnum } from '@lyearn/events';
import {
  CloseOutline,
  Edit2Outline,
  ExternalLinkFill,
  Link2Outline,
  PlusOutline,
} from '@lyearn/icons';
import { useReactNodeView } from '@lyearn/lygo-core';
import { Button, Dialog, Input, Stack, Tooltip, Typography } from '@lyearn/ui';

export interface RedirectLinkValue {
  url: string;
  heading: string;
  buttonTitle: string;
}

const EMPTY_VALUE: RedirectLinkValue = { url: '', heading: '', buttonTitle: '' };

const CERT_DISABLED_TOOLTIP =
  'Redirect Link is disabled because a Certificate is enabled. Remove the certificate to enable this.';

/**
 * Phone-shaped preview mirroring the consumption-time overlay: the block's own media with the
 * "Discover / heading / button" CTA card floating over the bottom, so authors see what learners get.
 */
function PreviewDevice({
  mediaSrc,
  heading,
  buttonTitle,
}: {
  mediaSrc?: string;
  heading: string;
  buttonTitle: string;
}) {
  return (
    <div className="relative w-[300px] min-h-[440px] shrink-0 self-stretch overflow-hidden rounded-24 bg-black">
      {mediaSrc ? (
        // eslint-disable-next-line @next/next/no-img-element -- decorative fixed-size preview, not page content
        <img alt="" className="absolute inset-0 h-full w-full object-cover" src={mediaSrc} />
      ) : null}
      <div className="absolute inset-x-12 bottom-16">
        <Stack
          alignItems="center"
          className="w-full justify-between gap-x-12 rounded-16 bg-white px-16 py-12 shadow-[0px_8px_32px_rgba(0,0,0,0.24),0px_2px_8px_rgba(0,0,0,0.12)]"
          direction="row">
          <Stack className="min-w-0" direction="col" gapY={1}>
            <Typography
              className="uppercase tracking-[1.2px] text-text-secondary opacity-60"
              variant="label-caps">
              Discover
            </Typography>
            <Typography
              className="truncate font-semibold text-text-primary"
              variant="body-short-02">
              {heading || 'Heading'}
            </Typography>
          </Stack>
          <span className="inline-flex shrink-0 items-center gap-x-4 rounded-18 bg-black px-12 py-8 text-sm font-semibold text-white">
            {buttonTitle || 'Claim Now'}
            <ExternalLinkFill className="h-14 w-14 fill-current" />
          </span>
        </Stack>
      </div>
    </div>
  );
}

/** Label + input pair used throughout the modal form. */
function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Stack className={className} direction="col" gapY={8}>
      <Typography className="font-semibold text-text-primary" variant="heading-xxs">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * Per-slide Redirect Link authoring UI for micro-learning media blocks. Renders an "Add" pill when
 * empty, or a summary row (label + Remove/Edit + heading/url card) once configured, plus a modal to
 * edit it. Persists into the block node's `redirectLink` attribute via `updateAttrs`.
 */
export default function RedirectLinkEditor({ disabled }: { disabled?: boolean }) {
  const { node, updateAttrs } = useReactNodeView();
  const redirectLink: RedirectLinkValue | null = node?.attrs?.redirectLink ?? null;
  const blockId: string | undefined = node?.attrs?.id;
  // Media shown in the modal preview: videos carry a still `thumbnail`; image blocks use `src`
  // directly (a video's `src` is a stream URL, unusable as an <img>).
  const mediaSrc: string | undefined =
    node?.type?.name === 'video' ? node?.attrs?.thumbnail : node?.attrs?.src;

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<RedirectLinkValue>(EMPTY_VALUE);
  // Certificate settings live in app-level state (a separate document from this editor), so we
  // can't reach it via context here — the app broadcasts changes over the shared event bus instead.
  // Seed from the emitter's last known value (not just `false`) so a component that mounts after
  // the one-time broadcast already fired — e.g. on remount, undo/redo, or lazy block mounting —
  // still starts with the correct disabled state instead of missing it.
  const [certificateEnabled, setCertificateEnabled] = useState(
    () => emitter.getLast(EventsEnum.CertificateSettingsUpdated)?.certificateEnabled ?? false,
  );

  const hasRedirectLink = Boolean(redirectLink?.url);

  // Tell app-level state (which can't see this editor's own document) whether this block
  // currently has a redirect link — covers initial load, saves, removes, and collaborator edits.
  useEffect(() => {
    if (!blockId) return;
    emitter.emit(EventsEnum.RedirectLinkUpdated, { blockId, hasRedirectLink });
  }, [blockId, hasRedirectLink]);

  useEffect(() => {
    const handleCertificateSettingsUpdated = ({
      certificateEnabled: enabled,
    }: {
      certificateEnabled: boolean;
    }) => {
      setCertificateEnabled(enabled);
    };
    emitter.on(EventsEnum.CertificateSettingsUpdated, handleCertificateSettingsUpdated);
    return () => {
      emitter.off(EventsEnum.CertificateSettingsUpdated, handleCertificateSettingsUpdated);
    };
  }, []);

  const isDisabled = disabled || certificateEnabled;

  const openModal = useCallback(() => {
    setDraft(redirectLink ?? EMPTY_VALUE);
    setOpen(true);
  }, [redirectLink]);

  const closeModal = useCallback(() => setOpen(false), []);

  const onSave = useCallback(() => {
    const trimmed: RedirectLinkValue = {
      url: draft.url.trim(),
      heading: draft.heading.trim(),
      buttonTitle: draft.buttonTitle.trim(),
    };
    updateAttrs({ redirectLink: trimmed.url ? trimmed : null });
    setOpen(false);
  }, [draft, updateAttrs]);

  const onRemove = useCallback(() => {
    updateAttrs({ redirectLink: null });
    setOpen(false);
  }, [updateAttrs]);

  const isSaveDisabled = useMemo(() => !draft.url.trim(), [draft.url]);

  return (
    <>
      {redirectLink ? (
        <Stack className="w-full gap-y-12 border-t border-border pt-16" direction="col">
          <Stack alignItems="center" direction="row" justifyContent="between">
            <Typography color="text-text-secondary" variant="body-short-01">
              Redirect Link
            </Typography>
            <Stack alignItems="center" direction="row" gapX={8}>
              {/* Remove stays enabled even when blocked by a certificate, so a user who ends up
                  in a conflicting state (e.g. legacy content) can always self-resolve it. */}
              <Button
                appearance="secondary"
                disabled={disabled}
                size="small"
                startIcon={<CloseOutline className="h-16 w-16 fill-current" />}
                variant="ghost"
                onClick={onRemove}>
                Remove
              </Button>
              <Tooltip
                placement="top"
                title={certificateEnabled ? CERT_DISABLED_TOOLTIP : undefined}>
                {/* Disabled elements don't fire hover events, so the Tooltip wraps a plain span
                    around the disabled Button instead of the Button itself. */}
                <span className="inline-block">
                  <Button
                    appearance="secondary"
                    disabled={isDisabled}
                    shape="pill"
                    size="small"
                    startIcon={<Edit2Outline className="h-16 w-16 fill-current" />}
                    variant="outlined"
                    onClick={openModal}>
                    Edit
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack
            alignItems="center"
            className="gap-x-16 rounded-12 bg-bg-tertiary px-16 py-12"
            direction="row"
            justifyContent="between">
            <Typography
              className="min-w-0 flex-1 truncate font-semibold text-text-primary"
              variant="heading-s">
              {redirectLink.heading || '(No heading set)'}
            </Typography>
            <Stack alignItems="center" className="min-w-0 max-w-[55%]" direction="row" gapX={8}>
              <Link2Outline className="h-20 w-20 shrink-0" fillColorToken="icon-secondary" />
              <Typography
                className="truncate text-text-secondary"
                title={redirectLink.url}
                variant="body-short-01">
                {redirectLink.url}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack className="w-full pt-16 " direction="row">
          <Tooltip placement="top" title={certificateEnabled ? CERT_DISABLED_TOOLTIP : undefined}>
            <span className="inline-block">
              <Button
                appearance="secondary"
                disabled={isDisabled}
                shape="pill"
                size="medium"
                startIcon={<PlusOutline className="h-20 w-20 fill-current" />}
                variant="outlined"
                onClick={openModal}>
                Add Redirect Link
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )}

      <Dialog classes={{ paper: '!w-[900px] !max-w-[95vw]' }} open={open} onClose={closeModal}>
        <Stack className="w-full gap-x-32 p-32" direction="row">
          <Stack className="min-w-0 flex-1 gap-y-24" direction="col" justifyContent="between">
            <Stack className="gap-y-24" direction="col">
              <Stack direction="col" gapY={4}>
                <Typography className="font-semibold text-text-primary" variant="heading-m">
                  Redirect Link
                </Typography>
                <Typography color="text-text-secondary" variant="body-short-01">
                  Add a link at the end to redirect the user to an external page
                </Typography>
              </Stack>

              <Stack className="gap-y-16" direction="col">
                <Field label="URL">
                  <Input
                    placeholder="https://www.example.com/"
                    value={draft.url}
                    onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                  />
                </Field>
                <Stack className="gap-x-16" direction="row">
                  <Field className="flex-1" label="Heading">
                    <Input
                      placeholder="Discover more"
                      value={draft.heading}
                      onChange={(e) => setDraft((d) => ({ ...d, heading: e.target.value }))}
                    />
                  </Field>
                  <Field className="flex-1" label="Button title">
                    <Input
                      placeholder="Claim Now"
                      value={draft.buttonTitle}
                      onChange={(e) => setDraft((d) => ({ ...d, buttonTitle: e.target.value }))}
                    />
                  </Field>
                </Stack>
              </Stack>

              {redirectLink ? (
                <Stack direction="row">
                  <Button
                    appearance="secondary"
                    shape="pill"
                    size="medium"
                    startIcon={<CloseOutline className="h-16 w-16 fill-current" />}
                    variant="outlined"
                    onClick={onRemove}>
                    Remove Redirection
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            <Stack className="gap-x-8" direction="row">
              <Button
                appearance="secondary"
                className="flex-1"
                shape="pill"
                size="large"
                variant="outlined"
                onClick={closeModal}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                className="flex-1"
                disabled={isSaveDisabled}
                shape="pill"
                size="large"
                variant="filled"
                onClick={onSave}>
                Save
              </Button>
            </Stack>
          </Stack>

          <PreviewDevice
            buttonTitle={draft.buttonTitle}
            heading={draft.heading}
            mediaSrc={mediaSrc}
          />
        </Stack>
      </Dialog>
    </>
  );
}
